import React from 'react';
import PropTypes from 'prop-types';

import { useClassName } from 'common/hooks';
import { ContentfulRichText } from 'common/components';

import ContentBannerCTA from './ContentBannerCTA';
import {
  getSize,
  getPosition,
  isTruthy,
  BANNER_SIZES,
  BANNER_POSITIONS,
} from './utils';

import './content-banner.less';

function Description({ description, className, style }) {
  if (!description) return null;

  return (
    <div className={className} style={style}>
      <ContentfulRichText value={description} />
    </div>
  );
}

const ContentBanner = React.forwardRef(function ContentBanner(
  {
    title,
    description,
    size,
    contentPosition,
    backgroundImage,
    mobileBackgroundImage,
    fullWidthMobile,
    ctas,
    className,
    style,
    contentStyle,
    titleStyle,
    descriptionStyle,
    ctaStyle,
    onCtaClick,
  },
  ref
) {
  const classNames = useClassName('ContentBanner');

  const desktopImage = backgroundImage;
  const mobileImage = mobileBackgroundImage || backgroundImage;

  return (
    <div
      ref={ref}
      className={[
        classNames('wrapper', getSize(size)),
        isTruthy(fullWidthMobile) && classNames('wrapper--full-width-mobile'),
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        '--banner-background': desktopImage ? `url(${desktopImage})` : 'none',
        '--banner-background-mobile': mobileImage
          ? `url(${mobileImage})`
          : 'none',
        ...style,
      }}>
      <div className={classNames('overlay')} />
      <div
        className={classNames('content', getPosition(contentPosition))}
        style={contentStyle}>
        {Boolean(title) && (
          <h3 className={classNames('title')} style={titleStyle}>
            {title}
          </h3>
        )}
        <Description
          description={description}
          className={classNames('description')}
          style={descriptionStyle}
        />
        {Boolean(ctas?.length) && (
          <div className={classNames('ctas')}>
            {ctas.map((cta, index) => (
              <ContentBannerCTA
                key={cta.id || cta.label || index}
                cta={cta}
                style={ctaStyle}
                onClick={onCtaClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

ContentBanner.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.oneOf(BANNER_SIZES),
  contentPosition: PropTypes.oneOf(BANNER_POSITIONS),
  backgroundImage: PropTypes.string,
  mobileBackgroundImage: PropTypes.string,
  fullWidthMobile: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  ctas: PropTypes.arrayOf(PropTypes.shape(ContentBannerCTA.ctaShape)),
  className: PropTypes.string,
  style: PropTypes.object,
  contentStyle: PropTypes.object,
  titleStyle: PropTypes.object,
  descriptionStyle: PropTypes.object,
  ctaStyle: PropTypes.object,
  onCtaClick: PropTypes.func,
};

export default ContentBanner;
